/**
 * Success Actions - Handle post-booking actions (webhooks, success pages, GHL tags)
 */

import {
  SuccessAction,
  WebhookSuccessAction,
  SuccessPageAction,
  GHLTagsSuccessAction,
  CombinedSuccessAction,
} from '../config/types';
import { GHLMiddleware, createGHLMiddleware } from './ghl-middleware';
import { GHLConfig } from '../config/types';

export interface SuccessActionContext {
  contactId?: string;
  bookingId?: string;
  appointmentId?: string;
  formData: Record<string, any>;
  bookingData?: Record<string, any>;
  ghlConfig?: GHLConfig;
}

/**
 * Execute a webhook success action
 */
async function executeWebhookAction(
  action: WebhookSuccessAction,
  context: SuccessActionContext
): Promise<void> {
  const { url, method = 'POST', headers = {}, payloadMapping } = action;
  const { formData, bookingData } = context;

  // Build payload from context
  let payload: Record<string, any> = {
    ...formData,
    ...bookingData,
    contactId: context.contactId,
    bookingId: context.bookingId,
    appointmentId: context.appointmentId,
  };

  // Apply payload mapping if provided
  if (payloadMapping) {
    const mappedPayload: Record<string, any> = {};
    for (const [key, sourceKey] of Object.entries(payloadMapping)) {
      mappedPayload[key] = payload[sourceKey];
    }
    payload = mappedPayload;
  }

  try {
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Webhook failed: ${response.status} ${response.statusText}`);
    }

    console.log('Webhook success action executed:', url);
  } catch (error) {
    console.error('Error executing webhook action:', error);
    // Don't throw - webhook failures shouldn't break the flow
  }
}

/**
 * Execute GHL tags success action
 */
async function executeGHLTagsAction(
  action: GHLTagsSuccessAction,
  context: SuccessActionContext
): Promise<void> {
  const { tags, locationId, apiKey } = action;
  const { contactId, ghlConfig } = context;

  if (!contactId) {
    console.warn('No contactId provided for GHL tags action');
    return;
  }

  // Use action-specific config or fall back to main GHL config
  const effectiveLocationId = locationId || ghlConfig?.locationId;
  const effectiveApiKey = apiKey || ghlConfig?.apiKey;

  if (!effectiveLocationId || !effectiveApiKey) {
    console.warn('GHL locationId and apiKey required for tags action');
    return;
  }

  try {
    const ghlMiddleware = createGHLMiddleware({
      locationId: effectiveLocationId,
      apiKey: effectiveApiKey,
      fieldMapping: ghlConfig?.fieldMapping || {},
      defaultTags: tags,
    });

    await ghlMiddleware.addTagsToContact(contactId, tags);
    console.log('GHL tags added successfully:', tags);
  } catch (error) {
    console.error('Error adding GHL tags:', error);
    // Don't throw - tag failures shouldn't break the flow
  }
}

/**
 * Execute a success page action (returns navigation info)
 */
function executeSuccessPageAction(
  action: SuccessPageAction,
  context: SuccessActionContext
): { route: string; component?: React.ComponentType<any> } {
  return {
    route: action.route,
    component: action.component,
  };
}

/**
 * Execute a combined success action
 */
async function executeCombinedAction(
  action: CombinedSuccessAction,
  context: SuccessActionContext
): Promise<{ route?: string; component?: React.ComponentType<any> }> {
  let successPage: { route?: string; component?: React.ComponentType<any> } = {};

  // Execute all actions
  for (const subAction of action.actions) {
    const result = await executeSuccessAction(subAction, context);
    if (result?.route) {
      successPage = result;
    }
  }

  return successPage;
}

/**
 * Execute a success action
 */
export async function executeSuccessAction(
  action: SuccessAction,
  context: SuccessActionContext
): Promise<{ route?: string; component?: React.ComponentType<any> } | void> {
  switch (action.type) {
    case 'webhook':
      await executeWebhookAction(action, context);
      return;
    
    case 'ghl-tags':
      await executeGHLTagsAction(action, context);
      return;
    
    case 'success-page':
      return executeSuccessPageAction(action, context);
    
    case 'combined':
      return executeCombinedAction(action, context);
    
    default:
      console.warn('Unknown success action type:', (action as any).type);
      return;
  }
}

/**
 * Execute multiple success actions
 */
export async function executeSuccessActions(
  actions: SuccessAction[],
  context: SuccessActionContext
): Promise<{ route?: string; component?: React.ComponentType<any> }> {
  let successPage: { route?: string; component?: React.ComponentType<any> } = {};

  for (const action of actions) {
    const result = await executeSuccessAction(action, context);
    if (result?.route) {
      successPage = result;
    }
  }

  return successPage;
}
